import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk, RootState } from '../..'
import { getNSDCAccreditation, createNSDCAccreditation, updateNSDCAccreditationRecord, updateNSDCAccreditationStatus } from '../../api/nsdcAccreditation'
import { NSDCAccreditation } from '../../types/NsdcAccreditation'
import { showSuccessMessage, showErrorNotification } from '../notifications/actions'

interface ApiError {
    response?: {
        status?: number
        data?: Record<string, unknown>
    }
}

export const initialNSDCAccreditationList: NSDCAccreditation.NSDCAccreditationListState = {
    data: [],
    meta: {
        page: 1,
        pageCount: 1,
        limit: 20,
    },
    filter: {
        pageSize: 20,
        page: 1,
    },
    isLoading: false,
    hasError: false,
}

const nsdcAccreditationListSlice = createSlice({
    name: 'nsdcAccreditationList',
    initialState: initialNSDCAccreditationList,
    reducers: {
        requestNSDCAccreditationList(state) {
            return { ...state, isLoading: true, hasError: false }
        },
        requestedNSDCAccreditationListFailed(state) {
            return { ...state, isLoading: false, hasError: true }
        },
        receivedNSDCAccreditationList(
            state,
            action: PayloadAction<NSDCAccreditation.NSDCAccreditationListPayload>,
        ) {
            const { data, filter, meta } = action.payload
            const newFilter: NSDCAccreditation.Filter = {
                ...state.filter,
                ...filter,
            }
            return { ...state, data, meta, filter: newFilter, isLoading: false, hasError: false }
        },
    },
})

export const {
    receivedNSDCAccreditationList,
    requestNSDCAccreditationList,
    requestedNSDCAccreditationListFailed,
} = nsdcAccreditationListSlice.actions

export const fetchNSDCAccreditation =
    (filter: NSDCAccreditation.Filter): AppThunk =>
        async dispatch => {
            dispatch(requestNSDCAccreditationList())
            try {
                const response = await getNSDCAccreditation(filter)
                if (response && response.data) {
                    // API returns Django REST Framework format: { count, next, previous, results }
                    const apiData: NSDCAccreditation.PaginatedResponse<NSDCAccreditation.Root> = response.data
                    const results = apiData.results || []
                    const count = apiData.count || 0
                    const pageSize = filter.pageSize || 20
                    const currentPage = filter.page || 1
                    const pageCount = Math.ceil(count / pageSize)

                    const meta = {
                        page: currentPage,
                        pageCount: pageCount || 1,
                        limit: pageSize,
                        totalObjectCount: count,
                    }

                    dispatch(
                        receivedNSDCAccreditationList({
                            data: results,
                            filter,
                            meta,
                        }),
                    )
                } else {
                    // If response exists but no data, dispatch empty result
                    dispatch(
                        receivedNSDCAccreditationList({
                            data: [],
                            filter,
                            meta: { page: 1, pageCount: 1, limit: filter.pageSize || 20, totalObjectCount: 0 },
                        }),
                    )
                }
            } catch (error) {
                console.error('DEBUG: API Error', error)
                dispatch(requestedNSDCAccreditationListFailed())
            }
        }

export const selectNSDCAccreditationList = (state: RootState) => state.nsdcAccreditationList

export const createNSDCAccreditationRecord =
    (nsdcAccreditation: Partial<NSDCAccreditation.Root>, filter?: NSDCAccreditation.Filter): AppThunk =>
        async dispatch => {
            try {
                const response = await createNSDCAccreditation(nsdcAccreditation as NSDCAccreditation.Root)
                if (response && response.data) {
                    dispatch(showSuccessMessage('NSDC Accreditation created successfully'))
                    // Refresh the list if filter is provided
                    if (filter) {
                        dispatch(fetchNSDCAccreditation(filter))
                    }
                    return response.data
                }
            } catch (error) {
                console.error('DEBUG: Create NSDC Accreditation Error', error)
                const apiError = error as ApiError
                if (apiError.response?.status === 400) {
                    const description = Object.keys(apiError.response?.data || {})
                        .map(e => String(apiError.response?.data?.[e]))
                        .join('\n')
                    dispatch(showErrorNotification(description))
                } else {
                    dispatch(showErrorNotification('Something went wrong while creating NSDC Accreditation'))
                }
                throw error
            }
        }

export const updateNSDCAccreditationRecordData =
    (id: number, nsdcAccreditation: Partial<NSDCAccreditation.Root>, filter?: NSDCAccreditation.Filter): AppThunk =>
        async dispatch => {
            try {
                const response = await updateNSDCAccreditationRecord(id, nsdcAccreditation)
                if (response && response.data) {
                    dispatch(showSuccessMessage('NSDC Accreditation updated successfully'))
                    // Refresh the list if filter is provided
                    if (filter) {
                        dispatch(fetchNSDCAccreditation(filter))
                    }
                    return response.data
                }
            } catch (error) {
                console.error('DEBUG: Update NSDC Accreditation Error', error)
                const apiError = error as ApiError
                if (apiError.response?.status === 400) {
                    const description = Object.keys(apiError.response?.data || {})
                        .map(e => String(apiError.response?.data?.[e]))
                        .join('\n')
                    dispatch(showErrorNotification(description))
                } else {
                    dispatch(showErrorNotification('Something went wrong while updating NSDC Accreditation'))
                }
                throw error
            }
        }

export const updateNSDCAccreditationRecordStatus =
    (id: number, nsdcAccreditation: Partial<NSDCAccreditation.Root>, filter?: NSDCAccreditation.Filter): AppThunk =>
        async dispatch => {
            try {
                const response = await updateNSDCAccreditationStatus(id, nsdcAccreditation)
                if (response && response.data) {
                    dispatch(showSuccessMessage('NSDC Accreditation status updated successfully'))
                    // Refresh the list if filter is provided
                    if (filter) {
                        dispatch(fetchNSDCAccreditation(filter))
                    }
                    return response.data
                }
            } catch (error) {
                console.error('DEBUG: Update NSDC Accreditation Status Error', error)
                const apiError = error as ApiError
                if (apiError.response?.status === 400) {
                    const description = Object.keys(apiError.response?.data || {})
                        .map(e => String(apiError.response?.data?.[e]))
                        .join('\n')
                    dispatch(showErrorNotification(description))
                } else {
                    dispatch(showErrorNotification('Something went wrong while updating NSDC Accreditation status'))
                }
                throw error
            }
        }

export default nsdcAccreditationListSlice.reducer